/**
 * The entry window's Sign-in tab — the ONE place a connector's sign-in is
 * chosen (item 80; it was split between this tab and the Settings tab's
 * authentication list):
 *
 *   1  Who signs in — no sign-in · one key for everyone · a key for each
 *      agent · each person with their own account (OAuth) · each person with
 *      their own key. Each option says what the person USING the agent will
 *      experience (item 69).
 *   2  How the service expects the key — only for the three choices with one.
 *   3  The key — only for one key for everyone (everyone else's is entered by
 *      whoever owns it).
 *
 * One save writes all of it (`PUT …/signin-settings`): the same stored fields
 * a Settings save and a Sign-in save wrote before. Moving a connector agents
 * rely on off its shared key says first how many agents will need their own.
 *
 * Only the organization that added it chooses; others read it. A server each
 * person signs in to lists its tools with the manager's OWN sign-in: "Connect
 * your account", then "List its tools".
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useLanguage } from '@hooks/useLanguage';
import { showNotification } from '@/utils/showNotification';
import { PrimaryAction } from '@modules/chatbots/components/ConnectorDialog';
import { SignInChoice } from '@modules/chatbots/components/SignInChoice';
import {
  keyTyped, libraryKeyFields, signInComplete, signInShape, storedSignIn, type SignInValue,
} from '@modules/chatbots/utils/signIn';
import {
  connectorLibraryService,
  errorBody,
  type LibraryEntryEditable,
} from '../services/connectorLibraryService';
import { myConnectorsService, type MyConnector } from '../services/myConnectorsService';
import { PersonalConnect } from './PersonalConnect';
import './LibraryOrganizations.css';

const OAUTH_FIELDS = ['authorization_url', 'token_url', 'revocation_url', 'scopes', 'client_id', 'client_secret'] as const;
type OAuthField = typeof OAUTH_FIELDS[number];

interface Props {
  tenantId: string;
  detail: LibraryEntryEditable;
  /** Opened right after adding, on the choice made there (never its key). */
  startSignIn?: SignInValue;
  onChanged: () => void;
}

export function LibrarySignIn({ tenantId, detail, startSignIn, onChanged }: Props) {
  const { t } = useLanguage();
  const stored = useMemo(() => storedSignIn(detail), [detail]);
  const owner = !detail.inherited;
  const [value, setValue] = useState<SignInValue>(() => (startSignIn ? { ...stored, ...startSignIn } : stored));
  const [oauth, setOauth] = useState(() => Object.fromEntries(OAUTH_FIELDS.map(k => [k, ''])) as Record<OAuthField, string>);
  const [busy, setBusy] = useState(false);
  const [keysNeeded, setKeysNeeded] = useState(0);
  const [mine, setMine] = useState<MyConnector | null>(null);

  // A saved (or reloaded) entry starts the answer again from what is stored.
  const seenVersion = useRef(detail.edit_version);
  useEffect(() => {
    if (seenVersion.current === detail.edit_version) return;
    seenVersion.current = detail.edit_version;
    setValue(stored);
    setKeysNeeded(0);
  }, [detail.edit_version, stored]);

  const current = stored.mode;
  const perPerson = current === 'per_person_oauth' || current === 'per_person_key';
  const reloadMine = () => {
    myConnectorsService.get(detail.entry_id).then(setMine).catch(() => setMine(null));
  };
  useEffect(() => {
    if (!perPerson) { setMine(null); return; }
    myConnectorsService.get(detail.entry_id).then(setMine).catch(() => setMine(null));
  }, [perPerson, detail.entry_id, detail.edit_version]);

  // The shared key stays only where it was entered: the same place, the same name.
  const keyStored = !!detail.has_shared_credential && current === 'shared' && signInShape({ ...value, mode: 'shared' }) === signInShape(stored);
  const oauthTyped = value.mode === 'per_person_oauth' && detail.kind === 'api' && OAUTH_FIELDS.some(k => oauth[k].trim());
  const dirty = signInShape(value) !== signInShape(stored) || keyTyped(value) || oauthTyped;
  const canSave = owner && dirty && !busy && signInComplete('library', value, keyStored);

  const change = (next: SignInValue) => { setValue(next); setKeysNeeded(0); };

  const save = async (confirmed: boolean) => {
    setBusy(true);
    try {
      // Off the shared key onto each agent's own: the agents that relied on it will need theirs.
      if (!confirmed && detail.has_shared_credential && value.mode === 'per_agent') {
        const preview = await connectorLibraryService.preview(detail.entry_id, tenantId, 'save', { share_credential: false });
        if (preview.keys_needed > 0) { setKeysNeeded(preview.keys_needed); return; }
      }
      await connectorLibraryService.saveSignIn(detail.entry_id, tenantId, value.mode, detail.edit_version,
        libraryKeyFields(detail.kind, value), value.mode === 'per_person_oauth' && detail.kind === 'api' ? oauth : undefined);
      showNotification(t('connectorLibrary.signin.saved'), 'success');
      setKeysNeeded(0);
      onChanged();
    } catch (err) {
      showNotification(errorBody(err).error || t('connectorLibrary.signin.saveError'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const listTools = async () => {
    setBusy(true);
    try {
      await connectorLibraryService.refresh(detail.entry_id, tenantId);
      showNotification(t('connectorLibrary.signin.toolsListed'), 'success');
      onChanged();
    } catch (err) {
      showNotification(errorBody(err).error || t('connectorLibrary.errors.refresh'), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="cxl-orgs" data-testid="library-signin">
      <p className="cxl-panel__lead">{t('connectorLibrary.signin.lead')}</p>
      <SignInChoice kind={detail.kind} scope="library" value={value} onChange={change} disabled={!owner || busy}
                    keyStored={keyStored} name={`signin-${detail.entry_id}`}>
        {owner && value.mode === 'per_person_oauth' && detail.kind === 'api' && (
          <div className="cxl-signin__oauth">
            {OAUTH_FIELDS.map(k => (
              <label key={k} className="cxl-signin__field">
                <span>{t(`connectorLibrary.signin.oauth.${k}`)}</span>
                <input className="form-input" dir="ltr" value={oauth[k]} type={k === 'client_secret' ? 'password' : 'text'}
                       autoComplete={k === 'client_secret' ? 'new-password' : 'off'} onChange={e => setOauth(prev => ({ ...prev, [k]: e.target.value }))} />
              </label>
            ))}
          </div>
        )}
      </SignInChoice>
      {/* The difference people trip over (item 69): an account is signed in to, a key is copied. */}
      <p className="cxl-signin__contrast" data-testid="library-signin-contrast">{t('connectorLibrary.signin.keyVsAccount')}</p>

      {owner && (
        <div className="cxl-orgs__footer">
          {keysNeeded > 0 ? (
            <>
              <span className="cxl-orgs__footer-note" role="alert" data-testid="library-signin-keys-needed">
                {t('connectorLibrary.impact.keysNeeded', { count: keysNeeded })}
              </span>
              <button type="button" className="btn-cancel" onClick={() => setKeysNeeded(0)} disabled={busy}
                      data-testid="library-signin-back">{t('connectorLibrary.impact.back')}</button>
              <PrimaryAction label={t('connectorLibrary.impact.saveAnyway')} busy={busy}
                             onClick={() => { void save(true); }} testId="library-signin-save-anyway" />
            </>
          ) : (
            <PrimaryAction label={t('connectorLibrary.signin.save')} busy={busy} disabled={!canSave}
                           onClick={() => { void save(false); }} testId="library-signin-save" />
          )}
        </div>
      )}

      {perPerson && mine && (
        <div className="cxl-signin__mine">
          <p className="cxl-panel__lead">{t('connectorLibrary.signin.yourAccount')}</p>
          <PersonalConnect connector={mine} onChanged={reloadMine} />
          {owner && detail.kind === 'mcp' && current === 'per_person_oauth' && mine.status === 'connected' && (
            <button type="button" className="cx-btn cx-btn--sm" disabled={busy} onClick={() => { void listTools(); }}
                    data-testid="library-signin-list-tools">
              {(detail.tool_count ?? 0) > 0 ? t('connectorLibrary.signin.relist') : t('connectorLibrary.signin.listTools')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
